import type { SortState } from './sort'

/**
 * Row model of the uploads table (YouTube "Vídeos publicados", TikTok
 * profile uploads) plus the pure ordering helpers — kept out of the
 * component so they're testable in the node test environment.
 */
export interface UploadRow {
  videoId: string
  language: string
  /** External platform ID (YouTube videoId or TikTok video_id). Used for display only. */
  externalId: string
  title: string
  views: number | null
  likes: number | null
  comments: number | null
  /** True when the upload originated outside Prism — render as an
   * external link to YouTube/TikTok with an "Externo" chip instead of
   * routing into the Prism project page (which doesn't exist). */
  isExternal?: boolean
  /** Where to send the user when ``isExternal=true`` (YouTube watch URL,
   * TikTok video URL). Ignored for prism rows. */
  externalUrl?: string
  /** The video on its platform, for the small link icon next to a Prism
   * row title (whose title links into the project page). */
  watchUrl?: string
  /** Optional thumbnail. Today only external rows ship one (cached on
   * upsert in ``youtube_external_stats``); prism rows lean on the
   * project page for visuals. */
  thumbnailUrl?: string
  /** Stable number in publication order (1 = first video on the channel).
   * When set, the first column shows it instead of the positional rank, so
   * it keeps identifying the video under any sort. */
  seq?: number
  /** When the video went up — ISO. Orders ``seq``; shown on hover of the ID. */
  publishedAt?: string | null
  /** YouTube ``publishAt`` while the video is still private, waiting for
   * its slot — ISO. Renders an "Agendado" chip until the time passes. */
  scheduledFor?: string | null
  /** Ranking ``final_score`` (0..1) of the Prism project. Null when the
   * video was never scored; absent on external rows, which have no project. */
  score?: number | null
  /** ``criteria_version`` the score was computed under — scores from
   * different versions aren't directly comparable. */
  criteriaVersion?: string | null
}

export type UploadSortCol = 'id' | 'views' | 'likes' | 'comments' | 'score'

/**
 * Numbers the rows in publication order (``seq`` 1 = oldest) and returns
 * them newest first. Numbering by date is what keeps an ID pointing at the
 * same video under any header sort. Undated rows count as the oldest; ties
 * keep their incoming order.
 */
export function numberByPublication(rows: readonly UploadRow[]): UploadRow[] {
  return rows
    .slice()
    .sort((a, b) => publishedMs(a) - publishedMs(b))
    .map((r, i) => ({ ...r, seq: i + 1 }))
    .reverse()
}

const comparators: Record<UploadSortCol, (a: UploadRow, b: UploadRow) => number> = {
  id: (a, b) => (a.seq ?? 0) - (b.seq ?? 0),
  views: (a, b) => (a.views ?? -1) - (b.views ?? -1),
  likes: (a, b) => (a.likes ?? -1) - (b.likes ?? -1),
  comments: (a, b) => (a.comments ?? -1) - (b.comments ?? -1),
  score: (a, b) => (a.score ?? -1) - (b.score ?? -1),
}

/**
 * Applies a header sort. "asc" is the natural reading order — oldest ID,
 * fewest views first — and desc reverses it; ``null`` keeps the incoming
 * order. Rows still waiting on stats (null) count as -1, like the retention
 * table's missing AVP.
 */
export function sortUploadRows(rows: UploadRow[], sort: SortState<UploadSortCol> | null): UploadRow[] {
  if (!sort) return rows
  const sorted = rows.slice().sort(comparators[sort.col])
  return sort.dir === 'desc' ? sorted.reverse() : sorted
}

function publishedMs(row: UploadRow): number {
  const t = row.publishedAt ? Date.parse(row.publishedAt) : NaN
  return Number.isFinite(t) ? t : 0
}
